import { ApolloLink, Observable } from '@apollo/client';
import type { FetchResult, NextLink, Operation } from '@apollo/client';

export type AutomaticAiqRequestKey = {
  lifecycleGeneration: number;
  settingsFingerprint: 'automatic' | 'manual';
  dispensaryId: string;
  userId: string | null;
  customerId: string | null;
  phoneNumber: string;
  dob: string;
  enterpriseId: string | null;
  isoRegionCode: string | null;
  pinDecision: 'blank' | 'skip';
  integration: 'alpineiq';
  origin: 'automatic-aiq';
};

type Observer<T> = {
  next: (value: T) => void;
  error: (error: unknown) => void;
  complete: () => void;
};

type Entry<T> = {
  key: AutomaticAiqRequestKey;
  observers: Set<Observer<T>>;
  upstream?: ReturnType<Observable<T>['subscribe']>;
  hasValue: boolean;
  value?: T;
  error?: unknown;
  terminal: 'complete' | 'error' | null;
};

const MAX_ENTRIES_PER_SCOPE = 8;
const CONTEXT_KEY = 'automaticAiqOneShot';

const keysMatch = (left: AutomaticAiqRequestKey, right: AutomaticAiqRequestKey): boolean =>
  left.lifecycleGeneration === right.lifecycleGeneration &&
  left.settingsFingerprint === right.settingsFingerprint &&
  left.dispensaryId === right.dispensaryId &&
  left.userId === right.userId &&
  left.customerId === right.customerId &&
  left.phoneNumber === right.phoneNumber &&
  left.dob === right.dob &&
  left.enterpriseId === right.enterpriseId &&
  left.isoRegionCode === right.isoRegionCode &&
  left.pinDecision === right.pinDecision &&
  left.integration === right.integration &&
  left.origin === right.origin;

export class AutomaticAiqRequestCoordinator {
  private entries: Array<Entry<FetchResult>> = [];

  execute(key: AutomaticAiqRequestKey, start: () => Observable<FetchResult>): Observable<FetchResult> {
    let entry = this.entries.find((candidate) => keysMatch(candidate.key, key));

    if (!entry) {
      if (this.entries.length >= MAX_ENTRIES_PER_SCOPE) {
        return new Observable((observer) => {
          observer.error(new Error('Automatic AIQ request scope capacity exceeded'));
        });
      }

      entry = {
        key,
        observers: new Set(),
        hasValue: false,
        terminal: null,
      };
      this.entries.push(entry);
      const claimedEntry = entry;
      claimedEntry.upstream = start().subscribe({
        next: (value) => {
          claimedEntry.hasValue = true;
          claimedEntry.value = value;
          claimedEntry.observers.forEach((observer) => observer.next(value));
        },
        error: (error) => {
          claimedEntry.error = error;
          claimedEntry.terminal = 'error';
          claimedEntry.observers.forEach((observer) => observer.error(error));
          claimedEntry.observers.clear();
        },
        complete: () => {
          if (!claimedEntry.hasValue) {
            const error = new Error('Automatic AIQ coordinator completed without a replayable value');
            claimedEntry.error = error;
            claimedEntry.terminal = 'error';
            claimedEntry.observers.forEach((observer) => observer.error(error));
            claimedEntry.observers.clear();
            return;
          }
          claimedEntry.terminal = 'complete';
          claimedEntry.observers.forEach((observer) => observer.complete());
          claimedEntry.observers.clear();
        },
      });
    }

    if (entry.terminal === 'complete' && !entry.hasValue) {
      return new Observable((observer) => {
        observer.error(new Error('Automatic AIQ coordinator completed without a replayable value'));
      });
    }

    const replayEntry = entry;
    return new Observable((observer) => {
      if (replayEntry.hasValue) {
        const { value } = replayEntry;
        if (value === undefined) {
          observer.error(new Error('Automatic AIQ coordinator completed without a replayable value'));
          return undefined;
        }
        observer.next(value);
      }
      if (replayEntry.terminal === 'error') {
        observer.error(replayEntry.error);
        return undefined;
      }
      if (replayEntry.terminal === 'complete') {
        if (!replayEntry.hasValue) {
          observer.error(new Error('Automatic AIQ coordinator completed without a replayable value'));
          return undefined;
        }
        observer.complete();
        return undefined;
      }

      replayEntry.observers.add(observer);
      return () => replayEntry.observers.delete(observer);
    });
  }

  reset(): void {
    this.entries.forEach((entry) => {
      entry.upstream?.unsubscribe();
      entry.observers.forEach((observer) => observer.complete());
      entry.observers.clear();
    });
    this.entries = [];
  }
}

type AutomaticAiqContext = {
  enabled: boolean;
  integration: string;
  origin: string;
  coordinator: unknown;
  key: AutomaticAiqRequestKey;
};

export const getAutomaticAiqOneShotContext = (
  coordinator: AutomaticAiqRequestCoordinator,
  key: AutomaticAiqRequestKey
): Record<string, AutomaticAiqContext> => ({
  [CONTEXT_KEY]: {
    enabled: true,
    integration: 'alpineiq',
    origin: 'automatic-aiq',
    coordinator,
    key,
  },
});

export const automaticAiqOneShotLink = new ApolloLink((operation: Operation, forward: NextLink) => {
  const context = operation.getContext()[CONTEXT_KEY] as AutomaticAiqContext | undefined;
  if (
    context?.enabled !== true ||
    context.integration !== 'alpineiq' ||
    context.origin !== 'automatic-aiq' ||
    !(context.coordinator instanceof AutomaticAiqRequestCoordinator)
  ) {
    return forward(operation);
  }

  return context.coordinator.execute(context.key, () => forward(operation));
});
